// apps/client/src/pages/DebugPage.tsx
//
// Debug page to test missions, referrals, and ads logic
// Uses simple HTML buttons - NO fancy UI
//

import { useState } from 'react';
import { useUserStore } from '../store/userStore';
import { useAuthStore } from '../store/authStore';

export function DebugPage() {
    const { user } = useAuthStore();
    const {
        chips,
        missions,
        missionsLoading,
        referralCode,
        referralStats,
        adWatchingPlacement,
        fetchMissions,
        claimReward,
        fetchReferralData,
        applyReferralCode,
        watchAd,
        refreshBalance,
    } = useUserStore();

    const [testCode, setTestCode] = useState('');
    const [log, setLog] = useState<string[]>([]);

    const addLog = (msg: string) => {
        const timestamp = new Date().toLocaleTimeString();
        setLog(prev => [`[${timestamp}] ${msg}`, ...prev.slice(0, 19)]);
        console.log(msg);
    };

    // === ACTIONS ===
    const handleFetchMissions = async () => {
        addLog('📋 Fetching missions...');
        await fetchMissions();
        addLog(`✅ Misiones cargadas: ${missions.length}`);
    };

    const handleClaimMission = async (missionId: string, missionName: string) => {
        addLog(`🎁 Claiming: ${missionName}...`);
        const result = await claimReward(missionId);
        if (result.success) {
            addLog(`✅ Recompensa: ${result.reward} chips`);
        } else {
            addLog(`❌ Error reclamando misión`);
        }
    };

    const handleWatchAd = async () => {
        addLog('📺 Watching ad (5 second simulation)...');
        const result = await watchAd();
        if (result.success) {
            addLog(`🎬 Anuncio visto -> Saldo actualizado: ${result.newBalance}`);
        } else {
            addLog(`❌ Error viendo anuncio`);
        }
    };

    const handleFetchReferrals = async () => {
        addLog('👥 Fetching referral data...');
        await fetchReferralData();
        addLog(`✅ Código: ${referralCode}`);
    };

    const handleApplyCode = async () => {
        if (!testCode) return;
        addLog(`🔗 Applying code: ${testCode}...`);
        const result = await applyReferralCode(testCode);
        if (result.success) {
            addLog(`✅ Referido aplicado correctamente`);
        } else {
            addLog(`❌ Error: ${result.message}`);
        }
    };

    const handleRefreshBalance = async () => {
        addLog('💰 Refreshing balance...');
        await refreshBalance();
        addLog(`✅ Saldo: ${chips} chips`);
    };

    return (
        <div style={{ padding: 20, fontFamily: 'monospace', background: '#1a0b2e', minHeight: '100vh', color: '#fff' }}>
            <h1 style={{ color: '#FFC107' }}>🔧 Debug Panel - Logic Test</h1>

            {/* USER INFO */}
            <section style={{ marginBottom: 20, padding: 10, background: '#222', borderRadius: 8 }}>
                <h2>👤 User Info</h2>
                <p>Name: {user?.firstName ?? 'Not logged in'}</p>
                <p>Chips: <strong style={{ color: '#FFC107' }}>{chips}</strong></p>
                <button onClick={handleRefreshBalance} style={btnStyle}>🔄 Refresh Balance</button>
            </section>

            {/* MISSIONS */}
            <section style={{ marginBottom: 20, padding: 10, background: '#222', borderRadius: 8 }}>
                <h2>📋 Missions ({missions.length})</h2>
                <button onClick={handleFetchMissions} disabled={missionsLoading} style={btnStyle}>
                    {missionsLoading ? '⏳ Loading...' : '📥 Fetch Missions'}
                </button>
                <div style={{ marginTop: 10, maxHeight: 200, overflow: 'auto' }}>
                    {missions.map(m => (
                        <div key={m.id} style={{ padding: 8, borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                                <strong>{m.name}</strong>
                                <span style={{ marginLeft: 10, color: '#888' }}>{m.currentCount}/{m.targetCount}</span>
                                <span style={{ marginLeft: 10, color: '#FFC107' }}>+{m.rewardChips}</span>
                            </div>
                            <div>
                                {m.canClaim ? (
                                    <button onClick={() => handleClaimMission(m.id, m.name)} style={{ ...btnStyle, background: '#4CAF50' }}>
                                        Claim
                                    </button>
                                ) : (
                                    <span style={{ color: m.status === 'CLAIMED' ? '#4CAF50' : '#888' }}>{m.status}</span>
                                )}
                            </div>
                        </div>
                    ))}
                    {missions.length === 0 && <p style={{ color: '#888' }}>No missions loaded</p>}
                </div>
            </section>

            {/* ADS */}
            <section style={{ marginBottom: 20, padding: 10, background: '#222', borderRadius: 8 }}>
                <h2>📺 Ads</h2>
                <button onClick={handleWatchAd} disabled={adWatchingPlacement !== null} style={{ ...btnStyle, background: '#9C27B0' }}>
                    {adWatchingPlacement ? '⏳ Watching (5s)...' : '▶️ Watch Ad (Mock)'}
                </button>
            </section>

            {/* REFERRALS */}
            <section style={{ marginBottom: 20, padding: 10, background: '#222', borderRadius: 8 }}>
                <h2>👥 Referrals</h2>
                <p>My Code: <strong style={{ color: '#00BCD4' }}>{referralCode ?? 'Not loaded'}</strong></p>
                <p>Total Referrals: {referralStats?.totalReferrals ?? 0} | Completed: {referralStats?.completedReferrals ?? 0}</p>
                <p>Total Earned: {referralStats?.totalEarned ?? 0} chips</p>
                <button onClick={handleFetchReferrals} style={btnStyle}>🔄 Fetch Referral Data</button>
                <div style={{ marginTop: 10 }}>
                    <input
                        type="text"
                        value={testCode}
                        onChange={e => setTestCode(e.target.value)}
                        placeholder="Enter referral code (BT-XXXXXX)"
                        style={{ padding: 8, width: 200, marginRight: 10 }}
                    />
                    <button onClick={handleApplyCode} style={btnStyle}>Apply Code</button>
                </div>
            </section>

            {/* LOG */}
            <section style={{ padding: 10, background: '#111', borderRadius: 8 }}>
                <h2>📜 Console Log</h2>
                <div style={{ maxHeight: 200, overflow: 'auto', fontSize: 12 }}>
                    {log.map((l, i) => (
                        <div key={i} style={{ color: l.includes('❌') ? '#f44336' : l.includes('✅') ? '#4CAF50' : '#aaa' }}>
                            {l}
                        </div>
                    ))}
                    {log.length === 0 && <p style={{ color: '#666' }}>No logs yet</p>}
                </div>
            </section>
        </div>
    );
}

const btnStyle: React.CSSProperties = {
    padding: '8px 16px',
    margin: 4,
    background: '#FFC107',
    color: '#000',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 'bold',
};
